import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFeedback } from '@/components/feedback/feedback-context'
import { ApiError } from '@/lib/api/http-client'

import {
  favoriteUserApp,
  installUserApp,
  listMarketApps,
  listMyApps,
  listUserAppTags,
  type UserApp,
  type UserAppTagCollection,
  unfavoriteUserApp,
  uninstallUserApp,
} from './api'

export type AppTab = 'market' | 'mine'

export function appErrorCopy(
  error: unknown,
  fallback: string,
  unauthorized: string,
  forbidden: string,
  channelUnavailable: string = forbidden,
) {
  if (!(error instanceof ApiError)) {
    return fallback
  }
  if (error.errorCode === 'application.channel_unavailable') {
    return channelUnavailable
  }

  if (error.status === 401) {
    return unauthorized
  }

  if (error.status === 403) {
    return forbidden
  }

  return fallback
}

function isUnauthorizedError(error: unknown) {
  return error instanceof ApiError && error.status === 401
}

function sortAppsWithFavoritesFirst(apps: UserApp[]) {
  return [...apps].sort((leftApp, rightApp) => {
    return Number(rightApp.is_favorite) - Number(leftApp.is_favorite)
  })
}

function useDebouncedSearch(value: string) {
  const [search, setSearch] = useState(value.trim())

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearch(value.trim())
    }, 250)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [value])

  return search
}

function useDelayedVisible(visible: boolean, delayMs = 180) {
  const [delayedVisible, setDelayedVisible] = useState(false)

  useEffect(() => {
    if (!visible) {
      const timeoutId = window.setTimeout(() => {
        setDelayedVisible(false)
      }, 0)

      return () => {
        window.clearTimeout(timeoutId)
      }
    }

    const timeoutId = window.setTimeout(() => {
      setDelayedVisible(true)
    }, delayMs)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [delayMs, visible])

  return visible && delayedVisible
}

export function useAppsPage() {
  const { t } = useTranslation('userApps')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showFeedback } = useFeedback()
  const [activeTab, setActiveTab] = useState<AppTab>('mine')
  const [searchInput, setSearchInput] = useState('')
  const [selectedTagId, setSelectedTagId] = useState('')
  const [selectedApp, setSelectedApp] = useState<UserApp | null>(null)
  const [pendingRemoveApp, setPendingRemoveApp] = useState<UserApp | null>(null)
  const search = useDebouncedSearch(searchInput)
  const tagCollection: UserAppTagCollection = activeTab === 'mine' ? 'installed' : 'market'

  const tagsQuery = useQuery({
    queryFn: () => listUserAppTags(tagCollection),
    queryKey: ['user-app-tags', tagCollection],
    retry: (failureCount, error) => !isUnauthorizedError(error) && failureCount < 1,
  })

  const validatedTagId = useMemo(() => {
    if (!selectedTagId || !tagsQuery.isSuccess) {
      return ''
    }
    return tagsQuery.data.some((tag) => tag.id === selectedTagId) ? selectedTagId : ''
  }, [selectedTagId, tagsQuery.data, tagsQuery.isSuccess])

  const activeListParams = useMemo(
    () => ({
      page: 1,
      pageSize: 100,
      search,
      tagId: validatedTagId || undefined,
    }),
    [search, validatedTagId],
  )

  useEffect(() => {
    if (selectedTagId && tagsQuery.isSuccess && !validatedTagId) {
      const timeoutId = window.setTimeout(() => {
        setSelectedTagId('')
      }, 0)
      return () => {
        window.clearTimeout(timeoutId)
      }
    }
    return undefined
  }, [selectedTagId, tagsQuery.isSuccess, validatedTagId])

  const myAppsQuery = useQuery({
    placeholderData: (previous) => previous,
    queryFn: () =>
      listMyApps({
        ...activeListParams,
        tagId: activeTab === 'mine' ? activeListParams.tagId : undefined,
      }),
    queryKey: [
      'user-apps',
      'mine',
      activeTab === 'mine' ? activeListParams : { ...activeListParams, tagId: undefined },
    ],
    retry: (failureCount, error) => !isUnauthorizedError(error) && failureCount < 1,
  })

  const marketAppsQuery = useQuery({
    placeholderData: (previous) => previous,
    queryFn: () =>
      listMarketApps({
        ...activeListParams,
        tagId: activeTab === 'market' ? activeListParams.tagId : undefined,
      }),
    queryKey: [
      'user-apps',
      'market',
      activeTab === 'market' ? activeListParams : { ...activeListParams, tagId: undefined },
    ],
    retry: (failureCount, error) => !isUnauthorizedError(error) && failureCount < 1,
  })

  const invalidateAppLists = async () => {
    await queryClient.invalidateQueries({ queryKey: ['user-apps'] })
    await queryClient.invalidateQueries({ queryKey: ['user-app-tags'] })
  }
  const patchCachedAppFavorite = (appId: string, isFavorite: boolean) => {
    queryClient.setQueriesData<{ items: { id: string; is_favorite?: boolean }[] }>(
      { queryKey: ['user-apps'] },
      (data) => {
        if (!data) {
          return data
        }

        const items = data.items.map((item) => {
          if (item.id !== appId) {
            return item
          }

          return {
            ...item,
            is_favorite: isFavorite,
          }
        })

        return { ...data, items }
      },
    )
  }

  const installMutation = useMutation({
    mutationFn: installUserApp,
    onSuccess: async () => {
      // 停留在当前 tab，便于连续添加多个应用。
      setSelectedApp(null)
      showFeedback({
        message: t('feedback.installSuccess'),
        scope: 'global',
        tone: 'success',
      })
      await invalidateAppLists()
    },
  })

  const uninstallMutation = useMutation({
    mutationFn: uninstallUserApp,
    onSuccess: async () => {
      setSelectedApp(null)
      setPendingRemoveApp(null)
      showFeedback({
        message: t('feedback.removeSuccess'),
        scope: 'global',
        tone: 'success',
      })
      await invalidateAppLists()
    },
  })

  const favoriteMutation = useMutation({
    mutationFn: async (app: UserApp) => {
      if (app.is_favorite) {
        await unfavoriteUserApp(app.id)
        return
      }
      await favoriteUserApp(app.id)
    },
    onSuccess: async (_data, app) => {
      const nextIsFavorite = !app.is_favorite

      patchCachedAppFavorite(app.id, nextIsFavorite)
      setSelectedApp((currentApp) =>
        currentApp?.id === app.id ? { ...currentApp, is_favorite: nextIsFavorite } : currentApp,
      )
      showFeedback({
        message: app.is_favorite ? t('feedback.unfavoriteSuccess') : t('feedback.favoriteSuccess'),
        scope: 'global',
        tone: 'success',
      })
      await invalidateAppLists()
    },
  })

  const activeQuery = activeTab === 'mine' ? myAppsQuery : marketAppsQuery
  const rawApps = activeQuery.data?.items
  const apps = useMemo(() => {
    const items = rawApps ?? []

    if (activeTab !== 'mine') {
      return items
    }

    return sortAppsWithFavoritesFirst(items)
  }, [activeTab, rawApps])
  const isInitialLoading = activeQuery.isLoading && !activeQuery.data
  const showGridSkeleton = useDelayedVisible(isInitialLoading)
  const hasActiveFilter = search.length > 0 || validatedTagId.length > 0
  const isBusy =
    installMutation.isPending || uninstallMutation.isPending || favoriteMutation.isPending
  const operationError = installMutation.error ?? uninstallMutation.error ?? favoriteMutation.error

  const categoryItems = useMemo(
    () => [
      { id: '', name: t('filters.all') },
      ...(tagsQuery.data ?? []).map((tag) => ({ id: tag.id, name: tag.name })),
    ],
    [tagsQuery.data, t],
  )

  const handleUseApp = async (app: UserApp) => {
    await navigate({
      search: { appId: app.id } as never,
      to: '/chat',
    })
  }

  const requestRemoveApp = (app: UserApp) => {
    setSelectedApp(null)
    setPendingRemoveApp(app)
  }

  return {
    activeQuery,
    activeTab,
    apps,
    categoryItems,
    favoriteMutation,
    handleUseApp,
    hasActiveFilter,
    installMutation,
    isBusy,
    isInitialLoading,
    operationError,
    pendingRemoveApp,
    requestRemoveApp,
    searchInput,
    selectedApp,
    setActiveTab,
    setPendingRemoveApp,
    setSearchInput,
    setSelectedApp,
    setSelectedTagId,
    showGridSkeleton,
    t,
    uninstallMutation,
    validatedTagId,
  }
}
